"use client";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { DotcomAuthContext } from "../context";
import { useFetchProfile } from "./hooks/use-fetch-profile";
import type { DotcomAuthSession, KitAuthConfig } from "../types";
import { useKitAuth } from "./hooks/use-kit-auth";
import { getAuthStatus } from "./utils/get-auth-state";

export type KitAuthSessionProperties = {
  authConfig: KitAuthConfig;
  children: ReactNode;
};
export const KitAuthSession = ({
  authConfig,
  children,
}: KitAuthSessionProperties) => {
  const {
    accessToken,
    error,
    isLoading: isLoadingKitAuth,
    handleLogout,
    handleLogin,
  } = useKitAuth();

  const {
    data: profile,
    error: profileError,
    isLoading: isLoadingProfile,
  } = useFetchProfile({
    accessToken,
    enabled: Boolean(accessToken),
    url: authConfig.PROFILE_URL,
  });

  const value = useMemo<DotcomAuthSession>(() => {
    const combinedError = error ?? profileError ?? null;

    const status = getAuthStatus({
      combinedError,
      isLoadingKitAuth,
      isLoadingProfile,
      profile,
    });

    return {
      accessToken,
      customerDetails: profile,
      error: combinedError,
      handleLogin,
      handleLogout,
      status,
    };
  }, [
    accessToken,
    error,
    profile,
    profileError,
    isLoadingKitAuth,
    isLoadingProfile,
    handleLogin,
    handleLogout,
  ]);

  return (
    <DotcomAuthContext.Provider value={value}>
      {children}
    </DotcomAuthContext.Provider>
  );
};
